/**
 * Registre des risques de l'avant-projet (§04 de la Project Identity Sheet).
 * Partagé par l'étape du wizard et l'onglet — l'écrire deux fois produirait
 * deux comportements d'édition divergents.
 *
 * Style : en-tête de tableau du guide (`#EDEDED`, 700, bordure basse 2px), pas
 * de radius, chip de niveau (seul composant à 6px de radius). Grille CSS plutôt
 * que `<Table>` pour que les colonnes s'empilent en mobile, comme le tableau
 * des fiches de `MilestoneReviewPanel`.
 */
import { Box, Button, Chip, IconButton, MenuItem, Select, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useTranslation } from 'react-i18next';
import { AP_RISK_LEVELS, type ApRisk, type ApRiskLevel } from '@altyor-kronos/shared-types';
import { colors } from '../../core/theme/theme';

const LEVEL_COLOR: Record<ApRiskLevel, 'success' | 'warning' | 'error'> = {
  low: 'success', medium: 'warning', high: 'error',
};

const GRID = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '1fr 120px 2fr 2fr 40px' },
  gap: 1.5,
  alignItems: 'center',
  px: 1.75,
  py: 1.25,
  borderBottom: `1px solid ${colors.lightGray}`,
} as const;

export interface ApRiskTableProps {
  risks: ApRisk[];
  canEdit: boolean;
  onChange: (next: ApRisk[]) => void;
}

export default function ApRiskTable({ risks, canEdit, onChange }: ApRiskTableProps) {
  const { t } = useTranslation();

  const patch = (i: number, field: keyof ApRisk, value: string) =>
    onChange(risks.map((r, j) => (i === j ? { ...r, [field]: value } : r)));

  return (
    <Box data-testid="ap-risk-table" sx={{ border: `1px solid ${colors.lightGray}` }}>
      <Box
        sx={{
          ...GRID,
          display: { xs: 'none', md: 'grid' },
          backgroundColor: colors.lightGray,
          fontSize: 11,
          fontWeight: 700,
          borderBottom: `2px solid ${colors.bodyText}`,
        }}
      >
        <Box>{t('projects.avantProjetTab.riskDomain')}</Box>
        <Box>{t('projects.avantProjetTab.riskLevel')}</Box>
        <Box>{t('projects.avantProjetTab.riskDescription')}</Box>
        <Box>{t('projects.avantProjetTab.riskMitigation')}</Box>
        <Box />
      </Box>

      {risks.map((r, i) => (
        <Box key={i} sx={GRID} data-testid={`ap-risk-row-${i}`}>
          {canEdit ? (
            <>
              <TextField size="small" variant="standard" value={r.domain}
                onChange={(e) => patch(i, 'domain', e.target.value)}
                slotProps={{ htmlInput: { 'data-testid': `ap-risk-domain-${i}` } }} />
              <Select size="small" value={r.level}
                onChange={(e) => patch(i, 'level', e.target.value)}
                SelectDisplayProps={{ 'data-testid': `ap-risk-level-${i}` } as React.HTMLAttributes<HTMLDivElement>}>
                {AP_RISK_LEVELS.map((l) => (
                  <MenuItem key={l} value={l}>{t(`projects.avantProjetTab.level.${l}`)}</MenuItem>
                ))}
              </Select>
              <TextField size="small" variant="standard" value={r.description ?? ''}
                onChange={(e) => patch(i, 'description', e.target.value)} />
              <TextField size="small" variant="standard" value={r.mitigation ?? ''}
                onChange={(e) => patch(i, 'mitigation', e.target.value)} />
              <IconButton size="small" data-testid={`ap-risk-delete-${i}`}
                onClick={() => onChange(risks.filter((_, j) => j !== i))}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </>
          ) : (
            <>
              <Typography variant="body2">{r.domain}</Typography>
              <Chip size="small" color={LEVEL_COLOR[r.level]} label={t(`projects.avantProjetTab.level.${r.level}`)} />
              <Typography variant="body2">{r.description ?? '—'}</Typography>
              <Typography variant="body2">{r.mitigation ?? '—'}</Typography>
              <Box />
            </>
          )}
        </Box>
      ))}

      {canEdit && (
        <Box sx={{ px: 1.75, py: 1 }}>
          <Button size="small" startIcon={<AddIcon />} data-testid="ap-risk-add"
            onClick={() => onChange([...risks, { domain: '', level: 'medium' }])}>
            {t('projects.avantProjetTab.addRisk')}
          </Button>
        </Box>
      )}
    </Box>
  );
}
